<template>
	<JBox>
		<!-- Actions -->
		<JFlex class="s-w-full s-justify-end s-p-2">
			<JButton variant="white" class="s-mr-2" @click="resetZoom">
				<svg width="20" height="20" fill="currentColor" x="0px" y="0px" viewBox="0 0 512 512" class="s-text-indigo-500">
					<g>
						<g>
							<path
								d="M492,0H344.212c-11.046,0-20,8.954-20,20s8.954,20,20,20h99.503L283.394,200.322c-7.811,7.811-7.811,20.474,0,28.284
			c7.81,7.81,20.473,7.811,28.284,0L472,68.284v99.503c0,11.046,8.954,20,20,20c11.046,0,20-8.954,20-20V20
			C512,9.115,503.154,0,492,0z"
							/>
						</g>
					</g>
					<g>
						<g>
							<path
								d="M228.606,283.394c-7.811-7.81-20.474-7.811-28.284,0L40,443.716v-99.503c0-11.046-8.954-20-20-20s-20,8.954-20,20V492
			c0,10.866,8.853,20,20,20h147.788c11.046,0,20-8.954,20-20c0-11.046-8.954-20-20-20H68.284l160.322-160.322
			C236.417,303.867,236.417,291.204,228.606,283.394z"
							/>
						</g>
					</g>
				</svg>
			</JButton>
			<JButton variant="white" class="s-mr-2" @click="zoomIn">
				<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" class="zoom-in w-6 h-6 s-text-indigo-500">
					<path d="M5 8a1 1 0 011-1h1V6a1 1 0 012 0v1h1a1 1 0 110 2H9v1a1 1 0 11-2 0V9H6a1 1 0 01-1-1z"></path>
					<path
						fill-rule="evenodd"
						d="M2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8zm6-4a4 4 0 100 8 4 4 0 000-8z"
						clip-rule="evenodd"
					></path>
				</svg>
			</JButton>
			<JButton variant="white" class="s-mr-2" @click="zoomOut">
				<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" class="zoom-out w-6 h-6 s-text-indigo-500">
					<path
						fill-rule="evenodd"
						d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
						clip-rule="evenodd"
					></path>
					<path fill-rule="evenodd" d="M5 8a1 1 0 011-1h4a1 1 0 110 2H6a1 1 0 01-1-1z" clip-rule="evenodd"></path>
				</svg>
			</JButton>
			<JButton variant="white" class="s-mr-2" @click="prevPage">
				<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" class="arrow-left w-6 h-6 s-text-indigo-500">
					<path
						fill-rule="evenodd"
						d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
						clip-rule="evenodd"
					></path>
				</svg>
			</JButton>
			<JButton variant="white" @click="nextPage">
				<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" class="arrow-right w-6 h-6 s-text-indigo-500">
					<path
						fill-rule="evenodd"
						d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
						clip-rule="evenodd"
					></path>
				</svg>
			</JButton>
		</JFlex>

		<!-- Preview -->
		<JBox class="s-p-4" v-if="isLoading">
			<JFlex class="s-bg-white s-p-4 s-rounded s-justify-between s-items-center">
				<div class="s-mr-2 s-text-gray-900 s-text-md">{{ $t("templates.loadingFileMessage") }}</div>
				<JSpinner variant="default" type="dots" :delay="0" />
			</JFlex>
		</JBox>
		<canvas v-else id="pdfViewer" />
	</JBox>
</template>

<script>
const pdfjs = require("pdfjs-dist/es5/build/pdf.js")
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry"

export default {
	props: {
		showPreview: {
			type: Boolean
		},
		previewData: {
			type: [String, Uint8Array]
		},
		isLoading: {
			type: Boolean
		}
	},

	data() {
		return {
			pdf: null,
			maxPages: null,

			height: undefined,
			width: undefined,
			pageNumPending: null,

			page: 1,
			scale: 1,
			increment: 0.25
		}
	},

	methods: {
		async fetchPdfData() {
			await pdfjs
				.getDocument(this.previewData)
				.promise.then(pdf => {
					this.pdf = pdf
					this.maxPages = pdf.numPages

					this.renderPage(this.page)
				})
				.catch(error => {
					// For some PDF pdfjs returns but it doesn't seem like anything is wrong with the PDF
					// We print the error to the console but ignore it to display it to user
					if (error?.message === "Invalid PDF structure.") {
						console.error(error)
						return
					}

					this.$emit("error", error)
				})
		},
		async renderPage(pageNumber) {
			this.pageRendering = true

			await this.pdf.getPage(pageNumber).then(async page => {
				let viewport = page.getViewport({ scale: this.scale })
				let pdfViewer = document.getElementById("pdfViewer")

				pdfViewer.height = viewport.height
				pdfViewer.width = viewport.width

				this.height = pdfViewer.height
				this.width = pdfViewer.width

				page
					.render({
						canvasContext: pdfViewer.getContext("2d"),
						viewport
					})
					.promise.then(() => {
						this.pageRendering = false
						if (this.pageNumPending !== null) {
							// New page rendering is pending
							this.renderPage(this.pageNumPending)
							this.pageNumPending = null
						}
					})
					.catch(error => {
						this.$emit("error", error)
						if (error.name === "RenderingCancelledException") {
							this.renderPage(pageNumber)
							return
						}
					})
			})
		},
		resetZoom() {
			this.scale = 1
		},
		zoomIn() {
			this.scale += this.increment
		},
		zoomOut() {
			if (this.scale <= this.increment) return
			this.scale -= this.increment
		},
		prevPage() {
			if (this.page === 1) {
				this.page = 1
			} else this.page--
		},
		nextPage() {
			if (this.page + 1 > this.maxPages) {
				this.page = this.maxPages
			} else this.page++
		},
		handleKeyDown(event) {
			if (event.ctrlKey && event.key === "ArrowRight") {
				event.preventDefault()
				this.nextPage()
			}
			if (event.ctrlKey && event.key === "ArrowLeft") {
				event.preventDefault()
				this.prevPage()
			}
			if ((event.ctrlKey && event.key === "+") || (event.ctrlKey && event.key === "ArrowUp")) {
				event.preventDefault()
				this.zoomIn()
			}
			if ((event.ctrlKey && event.key === "-") || (event.ctrlKey && event.key === "ArrowDown")) {
				event.preventDefault()
				this.zoomOut()
			}
		}
	},

	computed: {
		computedViewport() {
			return {
				height: this.height,
				width: this.width
			}
		}
	},

	watch: {
		computedViewport: {
			handler() {
				this.$emit("viewport:updated", this.computedViewport)
			},
			immediate: true,
			deep: true
		},
		scale: {
			handler() {
				this.renderPage(this.page)
			}
		},
		page: {
			handler() {
				this.renderPage(this.page)
			}
		},
		previewData: {
			handler() {
				this.fetchPdfData()
			}
		}
	},

	created() {
		pdfjs.disableFontFace = false
		pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker
		this.fetchPdfData()
	},

	mounted() {
		window.addEventListener("keydown", this.handleKeyDown)
	},

	beforeDestroy() {
		window.removeEventListener("keydown", this.handleKeyDown)
	}
}
</script>
